import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RULES } from "@/constants/rulesTestIds";
import { LIFECYCLE_LABELS, ruleTypeLabel, severityLabel } from "@/constants/rulesVocab";

export default function RuleList() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [rules, setRules] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const [{ data: b }, { data: rs }] = await Promise.all([
      api.get(`/brands/${brandId}`),
      api.get(`/brands/${brandId}/rules`),
    ]);
    setBrand(b);
    setRules(rs);
  };

  useEffect(() => {
    load();
  }, [brandId]);

  const filtered = statusFilter === "all" ? rules : rules.filter((r) => r.lifecycle_status === statusFilter);

  return (
    <div data-testid={RULES.listRoot}>
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">
            {brand?.name || "Brand"} &middot; Rules
          </p>
          <h1 className="font-serif text-5xl tracking-tight">Brand Rules</h1>
          <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
            Author guidance in your own words; Artisan stores it structurally,
            tests it against your reference output, and enforces it at every
            step of generation, edit, approval, and export.
          </p>
        </div>
        {user?.is_platform_admin && (
          <button
            data-testid={RULES.newRuleButton}
            onClick={() => navigate(`/brands/${brandId}/rules/new`)}
            className="px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium hover:bg-[#2A2F3C]"
          >
            Author a rule
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {["all", "draft", "active", "retired"].map((s) => (
          <button
            key={s}
            data-testid={`${RULES.statusFilter}-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] ${
              statusFilter === s
                ? "bg-[#1A1F2C] text-[#F8F5EE]"
                : "text-[#6B6358] border border-[#1A1F2C]/15 hover:border-[#1A1F2C]/40"
            }`}
          >
            {s} ({s === "all" ? rules.length : rules.filter((r) => r.lifecycle_status === s).length})
          </button>
        ))}
      </div>

      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10 max-w-5xl">
        {filtered.length === 0 && (
          <div className="px-6 py-10 text-sm text-[#6B6358]">
            No rules yet. {user?.is_platform_admin && "Click Author a rule to begin."}
          </div>
        )}
        {filtered.map((r) => (
          <Link
            key={r.rule_id}
            to={`/brands/${brandId}/rules/${r.rule_id}`}
            data-testid={`${RULES.rowPrefix}${r.rule_id}`}
            className="flex items-start justify-between gap-6 px-6 py-5 hover:bg-[#F8F5EE]"
          >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
                {ruleTypeLabel(r.rule_type)} &middot; Tier {r.rule_type && (["required_element_presence","required_disclaimer","offer_constraint","location_data_accuracy","asset_eligibility","image_restriction","postal_zone_integrity","print_production_constraint","permission_boundary","brand_note","punctuation_style"].includes(r.rule_type) ? 1 : 2)}
              </div>
              <div className="font-serif text-lg leading-snug">{r.explanation}</div>
            </div>
            <div className="text-right shrink-0">
              <SeverityBadge severity={r.severity} />
              <StatusBadge status={r.lifecycle_status} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">
      {severityLabel(severity)}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    draft: "bg-[#F8F5EE] text-[#6B6358] border-[#1A1F2C]/15",
    active: "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]",
    retired: "bg-transparent text-[#B5462F] border-[#B5462F]/40",
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] border ${colors[status] || ""}`}>
      {LIFECYCLE_LABELS[status] || status}
    </span>
  );
}
